// src/pages/PlanningList.js
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Layout from "../components/Layout";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost/securecontrol-backend";

const COLORS = {
  primary: '#0B3C5D',
  secondary: '#1E88C8',
  accent: '#4FC3F7',
  darkText: '#2E2E2E',
  lightBg: '#F5F7FA',
  white: '#FFFFFF',
  danger: '#f44336',
  success: '#4caf50',
  warning: '#ff9800',
  info: '#2196f3'
};

const STATUT_COLORS = {
  prevu: { bg: '#e3f2fd', text: '#1565c0', label: 'Prévu' },
  en_cours: { bg: '#fff3e0', text: '#ef6c00', label: 'En cours' },
  termine: { bg: '#e8f5e9', text: '#2e7d32', label: 'Terminé' },
  annule: { bg: '#ffebee', text: '#c62828', label: 'Annulé' },
};

const formatDateTime = (iso) => {
  if (!iso) return '-';
  const d = new Date(iso.replace(' ', 'T'));
  return d.toLocaleString('fr-FR', { weekday: 'short', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
};

const toInputDateTime = (d) => {
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const PlanningList = () => {
  const navigate = useNavigate();
  const [userInfo, setUserInfo] = useState(null);
  const [creneaux, setCreneaux] = useState([]);
  const [agents, setAgents] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const now = new Date();
  const dansUneHeure = new Date(now.getTime() + 60 * 60 * 1000);
  const [form, setForm] = useState({
    id_user: "",
    id_site: "",
    date_debut: toInputDateTime(now),
    date_fin: toInputDateTime(dansUneHeure),
    periode: "",
    notes: "",
  });

  useEffect(() => {
    const stored = localStorage.getItem("user");
    if (!stored) {
      navigate("/login");
      return;
    }
    try {
      const u = JSON.parse(stored);
      setUserInfo({ ...u, profile: Number(u.profile || u.poste || 0) });
    } catch {
      navigate("/login");
    }
  }, [navigate]);

  const authHeaders = () => ({
    headers: {
      Authorization: `Bearer ${localStorage.getItem("access_token")}`,
      "Content-Type": "application/json",
    },
  });

  const fetchAll = useCallback(async () => {
    if (!userInfo) return;
    setLoading(true);
    setError("");
    try {
      const [planningRes, usersRes, sitesRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/planning/list.php`, authHeaders()),
        axios.get(`${API_BASE_URL}/users/list.php?limit=1000`, authHeaders()),
        axios.get(`${API_BASE_URL}/sites/list.php?limit=1000`, authHeaders()),
      ]);
      setCreneaux(planningRes.data?.data || []);
      setAgents(usersRes.data?.users || []);
      setSites(sitesRes.data?.data || []);
    } catch (err) {
      setError(err.response?.data?.message || "Impossible de charger le planning.");
    } finally {
      setLoading(false);
    }
  }, [userInfo]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.id_user || !form.id_site) {
      alert("Sélectionnez un agent/contrôleur et un site.");
      return;
    }
    setSubmitting(true);
    try {
      const response = await axios.post(
        `${API_BASE_URL}/planning/create.php`,
        {
          id_user: parseInt(form.id_user, 10),
          id_site: parseInt(form.id_site, 10),
          date_debut: form.date_debut.replace('T', ' ') + ':00',
          date_fin: form.date_fin.replace('T', ' ') + ':00',
          periode: form.periode || null,
          notes: form.notes,
        },
        authHeaders()
      );
      if (response.data?.success) {
        setShowForm(false);
        setForm({ ...form, periode: "", notes: "" });
        fetchAll();
      } else {
        alert(response.data?.message || "Erreur lors de la création.");
      }
    } catch (err) {
      alert(err.response?.data?.message || "Erreur lors de la création.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id_planning) => {
    if (!window.confirm("Supprimer ce créneau de planning ?")) return;
    try {
      await axios.delete(`${API_BASE_URL}/planning/delete.php`, {
        ...authHeaders(),
        data: { id_planning },
      });
      fetchAll();
    } catch (err) {
      alert(err.response?.data?.message || "Erreur lors de la suppression.");
    }
  };

  // Regroupement par jour pour un affichage type agenda
  const groupes = creneaux.reduce((acc, c) => {
    const jour = (c.date_debut || '').split(' ')[0] || c.date_debut?.split('T')[0];
    if (!acc[jour]) acc[jour] = [];
    acc[jour].push(c);
    return acc;
  }, {});
  const joursTries = Object.keys(groupes).sort();

  if (userInfo && userInfo.profile !== 3) {
    return (
      <Layout>
        <div className="pagetitle">
          <h1>Planning</h1>
        </div>
        <div className="alert alert-warning m-4">
          Seuls les gestionnaires peuvent accéder à cette page.
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="pagetitle">
        <h1>Planning des équipes</h1>
      </div>

      <section className="section">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <p className="text-muted mb-0">
            Planifiez à l'avance les affectations de vos agents et contrôleurs sur vos sites.
          </p>
          <button
            className="btn"
            style={{ backgroundColor: COLORS.primary, color: COLORS.white, borderRadius: '8px', padding: '10px 20px' }}
            onClick={() => setShowForm((v) => !v)}
          >
            <i className="bi bi-calendar-plus me-2"></i>
            {showForm ? "Annuler" : "Nouveau créneau"}
          </button>
        </div>

        {showForm && (
          <div className="card mb-4" style={{ borderRadius: '10px', border: `1px solid ${COLORS.lightBg}` }}>
            <div className="card-body">
              <form onSubmit={handleCreate}>
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label fw-semibold">Agent / Contrôleur</label>
                    <select
                      className="form-select"
                      style={{ borderColor: '#c9d2dc' }}
                      value={form.id_user}
                      onChange={(e) => setForm({ ...form, id_user: e.target.value })}
                      required
                    >
                      <option value="">-- Choisir --</option>
                      {agents.filter(a => a.profile === '2' || a.profile === '4' || a.profile === 2 || a.profile === 4).map((a) => (
                        <option key={a.id} value={a.id}>
                          {a.prenom} {a.nom} ({a.profile_libelle || (String(a.profile) === '2' ? 'Contrôleur' : 'Agent')})
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label fw-semibold">Site</label>
                    <select
                      className="form-select"
                      style={{ borderColor: '#c9d2dc' }}
                      value={form.id_site}
                      onChange={(e) => setForm({ ...form, id_site: e.target.value })}
                      required
                    >
                      <option value="">-- Choisir --</option>
                      {sites.map((s) => (
                        <option key={s.id_site} value={s.id_site}>{s.nom_site}</option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label fw-semibold">Début</label>
                    <input
                      type="datetime-local"
                      className="form-control"
                      style={{ borderColor: '#c9d2dc' }}
                      value={form.date_debut}
                      onChange={(e) => setForm({ ...form, date_debut: e.target.value })}
                      required
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label fw-semibold">Fin</label>
                    <input
                      type="datetime-local"
                      className="form-control"
                      style={{ borderColor: '#c9d2dc' }}
                      value={form.date_fin}
                      onChange={(e) => setForm({ ...form, date_fin: e.target.value })}
                      required
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label fw-semibold">Période</label>
                    <select
                      className="form-select"
                      style={{ borderColor: '#c9d2dc' }}
                      value={form.periode}
                      onChange={(e) => setForm({ ...form, periode: e.target.value })}
                    >
                      <option value="">Non précisée</option>
                      <option value="jour">Journée</option>
                      <option value="soir">Soirée</option>
                    </select>
                  </div>
                  <div className="col-12">
                    <label className="form-label fw-semibold">Notes (optionnel)</label>
                    <textarea
                      className="form-control"
                      style={{ borderColor: '#c9d2dc' }}
                      rows={2}
                      value={form.notes}
                      onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    />
                  </div>
                  <div className="col-12">
                    <button type="submit" className="btn" disabled={submitting}
                      style={{ backgroundColor: COLORS.success, color: COLORS.white, borderRadius: '8px', padding: '10px 24px' }}>
                      {submitting ? "Création..." : "Créer le créneau"}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        )}

        {loading ? (
          <div className="text-center py-5"><div className="spinner-border" style={{ color: COLORS.primary }}></div></div>
        ) : error ? (
          <div className="alert alert-danger">{error}</div>
        ) : joursTries.length === 0 ? (
          <div className="alert alert-info">Aucun créneau de planning pour le moment.</div>
        ) : (
          joursTries.map((jour) => (
            <div key={jour} className="mb-4">
              <h6 className="fw-bold mb-2" style={{ color: COLORS.primary }}>
                {new Date(jour).toLocaleDateString('fr-FR', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' })}
              </h6>
              <div className="table-responsive">
                <table className="table align-middle" style={{ backgroundColor: COLORS.white, borderRadius: '8px' }}>
                  <thead>
                    <tr style={{ backgroundColor: COLORS.lightBg }}>
                      <th>Horaire</th>
                      <th>Agent / Contrôleur</th>
                      <th>Site</th>
                      <th>Période</th>
                      <th>Statut</th>
                      <th>Notes</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {groupes[jour].map((c) => {
                      const s = STATUT_COLORS[c.statut] || STATUT_COLORS.prevu;
                      return (
                        <tr key={c.id_planning}>
                          <td>{formatDateTime(c.date_debut)} → {formatDateTime(c.date_fin)}</td>
                          <td>{c.nom_complet} <span className="text-muted small">({c.profile === 2 ? 'Contrôleur' : 'Agent'})</span></td>
                          <td>{c.nom_site}</td>
                          <td>
                            {c.periode && (
                              <span className="badge" style={{
                                backgroundColor: c.periode === 'jour' ? '#fff8e1' : '#e8eaf6',
                                color: c.periode === 'jour' ? '#ff8f00' : '#3949ab'
                              }}>
                                <i className={`bi ${c.periode === 'jour' ? 'bi-sun' : 'bi-moon-stars'} me-1`}></i>
                                {c.periode === 'jour' ? 'Journée' : 'Soirée'}
                              </span>
                            )}
                          </td>
                          <td>
                            <span className="badge" style={{ backgroundColor: s.bg, color: s.text }}>{s.label}</span>
                          </td>
                          <td className="text-muted small">{c.notes || '-'}</td>
                          <td>
                            <button className="btn btn-sm" style={{ color: COLORS.danger }} onClick={() => handleDelete(c.id_planning)}>
                              <i className="bi bi-trash"></i>
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ))
        )}
      </section>
    </Layout>
  );
};

export default PlanningList;
